/**
 * Kündigungs-Dialog für einen Vertrag im Portal.
 *
 * Workflow:
 *  1. POST /portal/self-service/terminations/preview holt earliest_valid_date
 *  2. User wählt Wunschdatum (Default = earliest)
 *  3. Bei abweichendem Datum < earliest → override_justification Pflicht
 *  4. Textform-Unterschrift: typisierter Name + explizite Acknowledgment-Checkbox
 *  5. POST /portal/self-service/terminations
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, AlertTriangle, FileSignature } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import DateInput from '@/components/ui/date-input';

export default function PortalTerminationDialog({ contract, onClose, onSubmitted }) {
  const [preview, setPreview] = useState(null);
  const [requestedDate, setRequestedDate] = useState('');
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState('');
  const [signature, setSignature] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(true);

  useEffect(() => {
    api.post('/portal/self-service/terminations/preview', { contract_id: contract.id })
      .then((r) => {
        setPreview(r.data);
        setRequestedDate(r.data.earliest_valid_date);
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || 'Vorschau konnte nicht geladen werden');
        onClose();
      })
      .finally(() => setLoadingPreview(false));
  }, [contract.id, onClose]);

  const requestEarlier = preview && requestedDate < preview.earliest_valid_date;

  const submit = async () => {
    if (!ack) { toast.error('Bitte den Hinweis bestätigen'); return; }
    if (!signature.trim() || signature.trim().length < 2) { toast.error('Bitte den vollständigen Namen eingeben'); return; }
    if (requestEarlier && !override.trim()) { toast.error('Begründung für früheres Datum erforderlich'); return; }
    setBusy(true);
    try {
      await api.post('/portal/self-service/terminations', {
        contract_id: contract.id,
        requested_termination_date: requestedDate,
        reason: reason.trim() || null,
        override_justification: requestEarlier ? override.trim() : null,
        signature_name: signature.trim(),
        acknowledgment: true,
      });
      toast.success('Kündigung eingereicht. Du erhältst eine Bestätigung der Verwaltung.');
      onSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kündigung fehlgeschlagen');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="termination-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-rose-600" /> Vertrag kündigen
          </DialogTitle>
          <DialogDescription>
            Vertrag <strong>{contract.contract_number || contract.id}</strong> – verbindliche Kündigung mit Textform-Unterschrift.
          </DialogDescription>
        </DialogHeader>

        {loadingPreview ? (
          <div className="text-slate-400 text-sm py-4"><Loader2 className="w-4 h-4 inline animate-spin" /> Frühestmögliches Ende wird berechnet…</div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm space-y-1">
              <p><span className="text-slate-500">Vertrag:</span> <strong>{preview.contract_number}</strong></p>
              <p><span className="text-slate-500">Beginn:</span> {preview.start_date}</p>
              <p><span className="text-slate-500">Kündigungsfrist:</span> {preview.notice_period_days} Tage</p>
              <p><span className="text-slate-500">Frühestmögliches Ende:</span> <strong>{preview.earliest_valid_date}</strong></p>
            </div>

            <div>
              <Label>Wunsch-Kündigungsdatum</Label>
              <DateInput  value={requestedDate} onChange={(e) => setRequestedDate(e.target.value)} data-testid="termination-date" />
            </div>

            {requestEarlier && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                <p className="text-xs text-amber-700 flex items-center gap-1 mb-2">
                  <AlertTriangle className="w-3 h-3" /> Du wünschst ein früheres Datum als die reguläre Frist. Bitte begründen — die Verwaltung prüft individuell.
                </p>
                <Textarea rows={2} value={override} onChange={(e) => setOverride(e.target.value)} placeholder="z. B. Umzug ins Ausland zum 15.06." data-testid="termination-override" />
              </div>
            )}

            <div>
              <Label>Grund der Kündigung (optional)</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="z. B. Schulwechsel" data-testid="termination-reason" />
            </div>

            <div className="rounded-lg bg-brand-red/5 border border-brand-red/20 p-3 space-y-3">
              <p className="text-xs text-brand-red">
                <strong>Textform-Unterschrift:</strong> Mit Eingabe deines vollständigen Namens und Bestätigung der Checkbox erklärst du die Kündigung verbindlich gemäß § 126b BGB.
              </p>
              <div>
                <Label>Vor- und Nachname (Unterschrift)</Label>
                <Input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Max Mustermann" data-testid="termination-signature" />
              </div>
              <label className="flex items-start gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" data-testid="termination-ack" />
                <span>
                  Ich bestätige, dass ich den Vertrag <strong>{preview.contract_number}</strong> hiermit verbindlich zum gewählten Datum kündige.
                  Mir ist bewusst, dass ich Berechtigte:r oder gesetzliche:r Vertreter:in der/des Vertragspartner:in bin.
                </span>
              </label>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy || loadingPreview} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="termination-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Verbindlich kündigen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
